import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { configQuery } from "./config";
import { endingValue, floorValue, fxValue, GLOBAL_RULES_COPY as T, rrpValue } from "./global_rules";
import { gateRule, rrpQuery } from "./rrp";
import { SectionHead } from "./StrategyParts";

/** The rules outside every strategy (c1-global-rules). Read only: its one control opens the Config tab, so it carries `data-nonmutating`. */
export function GlobalRulesSection() {
  const navigate = useNavigate();
  const config = useQuery(configQuery());
  // The gate is one company rule served on every market's read, so one market's read is enough.
  const gate = useQuery(rrpQuery("UK")).data?.gate ?? null;
  const toConfig = (
    <Button variant="link" className="h-auto p-0" data-nonmutating data-config-link
      onClick={() => void navigate({ to: "/app/$area/$module/$tab", params: { area: "margin_engine", module: "precision_pricing", tab: "config" } })}>
      {T.edit}
    </Button>
  );
  const head = <SectionHead title={T.title} desc={T.desc} />;
  if (config.data === undefined && config.error) {
    return (
      <section className="flex flex-col gap-2" data-section="global-rules">
        {head}
        <Alert role="alert" variant="destructive" className="flex flex-wrap items-center justify-between gap-2">
          <AlertDescription>{T.unread}</AlertDescription>
          <Button size="sm" variant="outline" className="text-foreground hover:text-foreground" data-nonmutating onClick={() => void config.refetch()}>{T.retry}</Button>
        </Alert>
      </section>
    );
  }
  if (config.data === undefined) {
    return <section className="flex flex-col gap-2" data-section="global-rules">{head}<Skeleton className="h-24 w-full" /></section>;
  }
  const c = config.data;
  const rows = [
    { ...T.rrp, value: rrpValue(c), detail: gate ? gateRule(gate) : null, where: toConfig },
    { ...T.floor, value: floorValue(c), detail: null, where: toConfig },
    { ...T.fx, value: fxValue(c), detail: null, where: toConfig },
    { ...T.ending, value: endingValue(c), detail: null, where: <span className="text-sm text-ks-grey-1" data-no-control>{T.noControl}</span> },
  ];
  return (
    <section className="flex flex-col gap-2" data-section="global-rules">
      {head}
      <Card><CardContent className="py-2">
        <Table className="max-md:block" data-global-rules-layout="responsive">
          <TableHeader className="max-md:hidden"><TableRow>{T.columns.map((column) => <TableHead key={column}>{column}</TableHead>)}</TableRow></TableHeader>
          <TableBody className="max-md:block">
            {rows.map((row) => (
              <TableRow key={row.name} className="max-md:grid max-md:grid-cols-1 max-md:gap-2 max-md:py-3" data-global-rule={row.name}>
                <TableCell className="font-medium text-ks-ink max-md:block max-md:px-0 max-md:py-0">{row.name}</TableCell>
                <TableCell className="text-sm whitespace-normal text-ks-ink max-md:block max-md:px-0 max-md:py-0">
                  {row.rule}
                  {row.detail && <p className="text-xs text-ks-grey-1" data-rule-detail>{row.detail}</p>}
                </TableCell>
                <TableCell className="text-sm whitespace-normal tabular-nums max-md:block max-md:px-0 max-md:py-0" data-rule-value>{row.value}</TableCell>
                <TableCell className="max-md:block max-md:px-0 max-md:py-0">{row.where}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </section>
  );
}
